import React, { useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListSubheader,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import BlockIcon from '@mui/icons-material/Block';
import SearchIcon from '@mui/icons-material/Search';

export interface OptionItem {
  value: string;
  label: string;
  /** Contacts with it, across the whole CRM. */
  count?: number;
  /** A dot in this colour before the label (tags). */
  color?: string | null;
  /** A short note after the label, such as a country code. */
  note?: string;
  /** Rows with the same group sit under one heading, in the order given. */
  group?: string;
}

interface OptionListProps {
  /** Names the list for assistive technology: "Tags", "Countries". */
  label: string;
  items: Array<OptionItem>;
  selected: ReadonlyArray<string>;
  onToggle: (value: string) => void;
  /** A ⊘ on each row, to leave out people who have it (tags). */
  excluded?: ReadonlyArray<string>;
  onToggleExclude?: (value: string) => void;
  /** One choice at a time, shown as radio buttons. */
  single?: boolean;
  /** A search box over the list, with this placeholder. */
  searchPlaceholder?: string;
  /** For a list searched by the API: the text, and the rows are already matched. */
  query?: string;
  onQueryChange?: (query: string) => void;
  loading?: boolean;
  /** Rows drawn before "type to narrow". */
  limit?: number;
  maxHeight?: number;
  emptyText?: string;
  /** Under the list, such as how the rows combine. */
  footer?: React.ReactNode;
}

const count = (n: number) => n.toLocaleString('en-US');

const matches = (item: OptionItem, query: string) => {
  const q = query.trim().toLowerCase();
  return !q || item.label.toLowerCase().includes(q) || Boolean(item.note?.toLowerCase().includes(q));
};

/**
 * A searchable checklist with counts: the body of most filter panels. A row's
 * click includes it; the ⊘, where offered, leaves it out instead.
 *
 * Rows chosen when the panel opened are listed first and stay put as others are
 * ticked, so nothing jumps under the pointer.
 */
export const OptionList = ({
  label,
  items,
  selected,
  onToggle,
  excluded = [],
  onToggleExclude,
  single,
  searchPlaceholder,
  query: controlledQuery,
  onQueryChange,
  loading,
  limit = 100,
  maxHeight = 320,
  emptyText = 'Nothing matches.',
  footer,
}: OptionListProps) => {
  const [ownQuery, setOwnQuery] = useState('');
  const query = controlledQuery ?? ownQuery;
  const setQuery = onQueryChange ?? setOwnQuery;

  // Fixed on opening: what was chosen then is listed first.
  const [pinned] = useState(() => new Set([...selected, ...excluded]));

  const { first, rest, total } = useMemo(() => {
    const shown = onQueryChange ? items : items.filter((item) => matches(item, query));
    const top = shown.filter((item) => pinned.has(item.value));
    const others = shown.filter((item) => !pinned.has(item.value));
    const room = Math.max(0, limit - top.length);
    return { first: top, rest: others.slice(0, room), total: shown.length };
  }, [items, query, pinned, limit, onQueryChange]);

  const chosen = new Set(selected);
  const left = new Set(excluded);

  const row = (item: OptionItem) => {
    const isChosen = chosen.has(item.value);
    const isLeft = left.has(item.value);
    const icon = single ? (
      isChosen ? (
        <RadioButtonCheckedIcon color="primary" />
      ) : (
        <RadioButtonUncheckedIcon sx={{ color: '#94a3b8' }} />
      )
    ) : isLeft ? (
      <RemoveCircleIcon color="error" />
    ) : isChosen ? (
      <CheckBoxIcon color="primary" />
    ) : (
      <CheckBoxOutlineBlankIcon sx={{ color: '#94a3b8' }} />
    );

    return (
      <ListItem
        key={item.value}
        disablePadding
        secondaryAction={
          onToggleExclude && (
            <Tooltip title={isLeft ? 'Stop leaving out' : 'Leave out people with this'} placement="left">
              <IconButton
                edge="end"
                size="small"
                aria-label={`Leave out ${item.label}`}
                aria-pressed={isLeft}
                onClick={() => onToggleExclude(item.value)}
                sx={{
                  color: isLeft ? 'error.main' : '#94a3b8',
                  opacity: isLeft ? 1 : 0.7,
                  '&:hover': { color: 'error.main', opacity: 1 },
                }}
              >
                <BlockIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          )
        }
      >
        <ListItemButton
          role={single ? 'radio' : 'checkbox'}
          aria-checked={isChosen}
          aria-label={item.count === undefined ? item.label : `${item.label}, ${count(item.count)} contacts`}
          onClick={() => onToggle(item.value)}
          dense
          sx={{
            borderRadius: 1.5,
            gap: 1.25,
            py: 0.5,
            pr: onToggleExclude ? 6 : 1,
            '& svg': { fontSize: 20 },
          }}
        >
          <Box component="span" aria-hidden sx={{ display: 'inline-flex', flexShrink: 0 }}>
            {icon}
          </Box>
          {item.color !== undefined && (
            <Box
              component="span"
              aria-hidden
              sx={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, bgcolor: item.color ?? '#cbd5e1' }}
            />
          )}
          <Box
            component="span"
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: '0.875rem',
              ...(isLeft ? { textDecoration: 'line-through', color: 'error.main' } : {}),
              ...(isChosen ? { fontWeight: 600 } : {}),
            }}
          >
            {item.label}
            {item.note && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                {item.note}
              </Typography>
            )}
          </Box>
          {item.count !== undefined && (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
            >
              {count(item.count)}
            </Typography>
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  // Group headings, only where the group changes.
  const withHeadings = (rows: Array<OptionItem>) => {
    const out: Array<React.ReactNode> = [];
    let group: string | undefined;
    rows.forEach((item) => {
      if (item.group && item.group !== group) {
        group = item.group;
        out.push(
          <ListSubheader
            key={`group-${group}`}
            disableSticky={false}
            sx={{ lineHeight: '28px', fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.secondary', bgcolor: 'background.paper' }}
          >
            {group}
          </ListSubheader>
        );
      }
      out.push(row(item));
    });
    return out;
  };

  const drawn = first.length + rest.length;

  return (
    <Box>
      {searchPlaceholder && (
        <TextField
          fullWidth
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={searchPlaceholder}
          inputProps={{ 'aria-label': searchPlaceholder }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: loading ? (
              <InputAdornment position="end">
                <CircularProgress size={16} aria-label="Searching" />
              </InputAdornment>
            ) : undefined,
          }}
          sx={{ mb: 1 }}
        />
      )}

      <List
        dense
        disablePadding
        aria-label={label}
        role={single ? 'radiogroup' : undefined}
        sx={{ maxHeight, overflowY: 'auto', mx: -1, px: 0.5 }}
      >
        {first.map(row)}
        {first.length > 0 && rest.length > 0 && <Divider component="li" sx={{ my: 0.5 }} />}
        {withHeadings(rest)}
      </List>

      {drawn === 0 && !loading && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          {emptyText}
        </Typography>
      )}
      {total > drawn && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
          Showing {count(drawn)} of {count(total)}. Type to narrow.
        </Typography>
      )}
      {footer}
    </Box>
  );
};
